import { useEffect, useRef, useState } from 'react';

const DEFAULT_INIT_TIMEOUT_MS = 8_000;

type SettledState = 'pending' | 'resolved' | 'rejected' | 'timedOut';

type InitCleanup = () => void | Promise<unknown>;

export const useOfflineProviderInit = (
  onInit: () => Promise<InitCleanup | void>,
  onSync?: () => Promise<void>
) => {
  const [initError, setInitError] = useState<Error | null>(null);
  const [initTimedOut, setInitTimedOut] = useState(false);
  const syncStarted = useRef(false);
  const settledRef = useRef<SettledState>('pending');
  // Ref, not effect dep: onSync identity churn must not restart the replication.
  const onSyncRef = useRef(onSync);
  onSyncRef.current = onSync;
  // Pending teardown of the previous init; the next init awaits it.
  const teardownRef = useRef<Promise<unknown> | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let startedInit = false;
    let cleanupFn: InitCleanup | null = null;
    let initSettled: Promise<unknown> = Promise.resolve();

    if (!syncStarted.current) {
      syncStarted.current = true;
      startedInit = true;
      settledRef.current = 'pending';
      setInitError(null);
      setInitTimedOut(false);

      // Timer starts with onInit, not at effect entry: the wait for a queued
      // teardown is not part of the init's own timeout budget.
      const startInit = () => {
        if (cancelled) {
          return undefined;
        }
        timeoutId = setTimeout(() => {
          if (cancelled || settledRef.current !== 'pending') {
            return;
          }
          settledRef.current = 'timedOut';
          setInitTimedOut(true);
        }, DEFAULT_INIT_TIMEOUT_MS);
        return onInit();
      };
      const pendingTeardown = teardownRef.current;

      initSettled = Promise.resolve(
        pendingTeardown
          ? pendingTeardown.catch(() => undefined).then(startInit)
          : startInit()
      )
        .then((cleanup) => {
          if (timeoutId !== null) {
            clearTimeout(timeoutId);
          }
          if (cancelled) {
            return cleanup?.();
          }
          cleanupFn = cleanup ?? null;
          if (settledRef.current === 'timedOut') {
            settledRef.current = 'resolved';
            setInitTimedOut(false);
            return;
          }
          settledRef.current = 'resolved';
          return;
        })
        .catch((err) => {
          if (timeoutId !== null) {
            clearTimeout(timeoutId);
          }
          if (cancelled) {
            return;
          }
          if (settledRef.current === 'timedOut') {
            setInitTimedOut(false);
          }
          settledRef.current = 'rejected';
          setInitError(err instanceof Error ? err : new Error(String(err)));
        });
    }

    const handleVisibility = () => {
      const sync = onSyncRef.current;
      return (
        sync &&
        document.visibilityState === 'visible' &&
        sync().catch(() => void 0)
      );
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener('visibilitychange', handleVisibility);
      syncStarted.current = false;
      if (!startedInit) {
        return;
      }
      teardownRef.current = cleanupFn
        ? Promise.resolve(cleanupFn()).catch(() => undefined)
        : initSettled;
      cleanupFn = null;
    };
  }, [onInit]);

  return { initError, initTimedOut };
};
